// The correction sheet: rewrite what Oreia said, or make it unlearn it.
//
// This is the screen's second promise made operable. Rewriting sends the user's plaintext to the
// worker, which re-seals it — and from then on their sentence is the input to future reasoning,
// not the original. Forgetting writes to an exclusion set, which is the difference between a
// memory that is hidden and one that is actually gone; without it the next nightly pass would
// re-derive the thing they just erased.
//
// The sources stay visible while correcting, because the honest question a user is answering here
// is "given these entries, is that a fair thing to say?".

import { useState } from 'react';
import { JournalSheet } from '../journals/JournalSheet';
import { IconBook, IconTrash } from '../components/Icons';
import type { Observation } from '../api/portrait';

export function AdjustMemorySheet({
  observation,
  entryTitle,
  onSave,
  onForget,
  onClose,
}: {
  observation: Observation;
  entryTitle: (id: string) => string | null;
  onSave(text: string): void;
  onForget(): void;
  onClose(): void;
}) {
  const [text, setText] = useState(observation.text);
  const trimmed = text.trim();

  return (
    <JournalSheet
      title="Adjust this memory"
      confirmLabel="Save"
      onConfirm={() => {
        if (trimmed && trimmed !== observation.text) onSave(trimmed);
        else onClose();
      }}
      onClose={onClose}
    >
      <div className="csheet-scroll">
        <p className="acorrect-hint">
          Rewrite it in your own words, or forget it entirely. Oreia will use your version and stop
          drawing the old conclusion.
        </p>

        <textarea
          className="acorrect-in"
          aria-label="The observation, in your words"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />

        {observation.sourceEntryIds.length > 0 ? (
          <>
            <div className="asrc" style={{ marginTop: 14 }}>
              {observation.sourceEntryIds.slice(0, 4).map((id, i) => (
                <span className="schip" key={id}>
                  <IconBook />
                  {entryTitle(id) || `Entry ${i + 1}`}
                </span>
              ))}
            </div>
            {observation.trace ? (
              <p className="acorrect-hint" style={{ marginTop: 10, marginBottom: 0 }}>
                {observation.trace}.
              </p>
            ) : null}
          </>
        ) : null}

        <button type="button" className="acorrect-forget" onClick={onForget}>
          <IconTrash />
          Forget this memory
        </button>
        <div style={{ height: 20 }} />
      </div>
    </JournalSheet>
  );
}
